import { FullEntityResponse } from 'app/modules/entities/responses';

import { ENTITY_QUERY_KEYS } from 'app/modules/entities/queries/keys';

import { get } from 'app/shared/utils/fetchr';
import { useQuery } from '@tanstack/react-query';
import { selectDLEnabledForEntityDetails } from 'app/shared/featureFlags/selectors';
import { useSelector } from 'react-redux';
import { selectHasReadEntitiesPermissions } from 'app/modules/session/selectors';
import { useRef, useMemo, useEffect } from 'react';
import { CGDOLoadingResponse } from 'app/modules/investigations/types/responses';
import {
  getLoadingFromColdStorageStateForCGDO,
  COLD_STORAGE_WAIT_TIME,
  COLD_STORAGE_MAX_RETRIES,
} from 'app/shared/utils/fastArchHelpers';

type EntityDetailsResponse = FullEntityResponse | CGDOLoadingResponse;

// Typescript guard to know if the data is loading or not
const isLoadingResponse = (
  data: EntityDetailsResponse,
): data is CGDOLoadingResponse => {
  return 'data_loader' in data;
};

export const useEntityDetails = <T = FullEntityResponse>(
  entityId: string,
  select: (data: FullEntityResponse) => T = (data) => data as T,
  enabled: boolean = true,
  isExternalEntityId: boolean = false,
) => {
  const useCacheEndpoint = useSelector(selectDLEnabledForEntityDetails);
  const hasReadEntitiesPermission = useSelector(
    selectHasReadEntitiesPermissions,
  );
  const dataUpdateCountRef = useRef(0);

  const query = useQuery<EntityDetailsResponse>({
    queryFn: () => {
      if (useCacheEndpoint) {
        return get<EntityDetailsResponse>(
          `/entities/cached/${encodeURIComponent(entityId)}`,
        );
      }
      if (isExternalEntityId) {
        return get<FullEntityResponse>(
          `/unit21/entities/${encodeURIComponent(entityId)}`,
        );
      }
      return get<FullEntityResponse>(
        `/entities/${encodeURIComponent(entityId)}`,
      );
    },
    queryKey: ENTITY_QUERY_KEYS.getEntityDetails(
      entityId,
      useCacheEndpoint,
      isExternalEntityId,
    ),
    staleTime: 10000,
    enabled: !!entityId && enabled && hasReadEntitiesPermission,
    refetchInterval: ({ state: { data } }) => {
      if (useCacheEndpoint) {
        if (
          data &&
          isLoadingResponse(data) &&
          dataUpdateCountRef.current < COLD_STORAGE_MAX_RETRIES
        ) {
          return COLD_STORAGE_WAIT_TIME;
        }
      }
      // Do not retry by default
      return false;
    },
  });

  const coldStorageLoading = useMemo(
    () =>
      getLoadingFromColdStorageStateForCGDO(
        entityId,
        query.data && isLoadingResponse(query.data)
          ? query.data.data_loader
          : undefined,
      ),
    [entityId, query.data],
  );

  useEffect(() => {
    // Reset update count when the ID changes
    dataUpdateCountRef.current = 0;
  }, [entityId]);

  // Increase the countRef whenever we get a loading response
  useEffect(() => {
    if (query.data && isLoadingResponse(query.data)) {
      dataUpdateCountRef.current += 1;
    }
  }, [query.data]);

  const isLoading = useMemo(() => {
    if (query.isLoading) {
      return true;
    }
    if (query.data && isLoadingResponse(query.data)) {
      return true;
    }
    return false;
  }, [query.isLoading, query.data]);

  const data: T | undefined = useMemo(() => {
    if (!query.data || isLoadingResponse(query.data)) {
      return undefined;
    }
    return select ? select(query.data) : (query.data as T);
  }, [query.data, select]);

  return {
    // This is still accesible if needed for whatever reason
    query,
    // The following values already take into account the cold storage loading state
    data,
    isLoading,
    coldStorageLoading,
  };
};
